/**
 * Utility functions for fetching player images dynamically
 */

import type { Sport } from '@sharpr/types';

/**
 * Sanitize player ID to prevent URL injection
 * Only allows alphanumeric characters and hyphens
 */
function sanitizePlayerId(playerId: string): string | null {
  // Remove any characters that aren't alphanumeric or hyphen
  const sanitized = playerId.replace(/[^a-zA-Z0-9-]/g, '');

  // Return null if sanitization resulted in empty string
  if (!sanitized || sanitized.length === 0) {
    console.warn(`[PlayerImages] Invalid player ID after sanitization: "${playerId}"`);
    return null;
  }

  // Warn if sanitization changed the ID (potential security issue)
  if (sanitized !== playerId) {
    console.warn(`[PlayerImages] Player ID sanitized from "${playerId}" to "${sanitized}"`);
  }

  return sanitized;
}

/**
 * Generate player image URL
 * Uses official CDN headshots when player ID is available, falls back to initials avatar
 */
export function getPlayerImageUrl(
  _playerName: string,
  sport: Sport,
  _teamAbbr?: string,
  playerId?: string | null
): string | null {
  if (playerId) {
    const sanitizedId = sanitizePlayerId(playerId);
    if (sanitizedId) {
      if (sport === 'mlb') {
        // MLB headshots via MLB static CDN
        return `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_213,q_auto:best/v1/people/${sanitizedId}/headshot/67/current`;
      }
      // Official NBA CDN - uses stats.nba.com player IDs
      return `https://cdn.nba.com/headshots/nba/latest/1040x760/${sanitizedId}.png`;
    }
  }

  // No player ID available — return null so components render initials fallback
  return null;
}

/** Official NBA CDN headshot URL keyed by stats.nba.com player_id. Tight,
 *  non-null variant for NBA-only call sites that always have an id and handle
 *  load failures via an `<img onError>` handler rather than an initials
 *  fallback. Same URL pattern as `getPlayerImageUrl(name, 'nba', _, id)`. */
export function getNbaHeadshotUrl(playerId: string | number): string {
  const sanitized = sanitizePlayerId(String(playerId));
  if (!sanitized) return '';
  return `https://cdn.nba.com/headshots/nba/latest/1040x760/${sanitized}.png`;
}
